import { useEffect, useState } from "react";
import { Link } from "wouter";
import { ArrowLeft, ArrowRight, Info } from "lucide-react";
import ValueChainImportanceForm from "@/components/assessment/ValueChainImportanceForm";
import AssetForm from "@/components/assessment/AssetForm";
import AssetList from "@/components/assessment/AssetList";
import { AssessmentTabs } from "@/components/assessment/AssessmentTabs";
import { ExcelUploadDialog } from "@/components/assessment/ExcelUploadDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  deleteAsset,
  listAssetTypes,
  listAssets,
  listValueChainStages,
  type Asset,
  type AssetType,
  type ValueChainStage,
} from "@/lib/assets";

function Assessment() {
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [stages, setStages] = useState<ValueChainStage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingAsset, setEditingAsset] = useState<Asset | null | undefined>(undefined); // undefined = form closed

  async function refreshAssets() {
    setError(null);
    try {
      const [assetsResult, typesResult, stagesResult] = await Promise.all([
        listAssets(),
        listAssetTypes(),
        listValueChainStages(),
      ]);
      setAssets(assetsResult.assets);
      setAssetTypes(typesResult.assetTypes);
      setStages(stagesResult.stages);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load assets");
    }
  }

  useEffect(() => {
    refreshAssets();
  }, []);

  function handleFormSaved() {
    setEditingAsset(undefined);
    refreshAssets();
  }

  async function handleDelete(asset: Asset) {
    if (!confirm(`Delete "${asset.name}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await deleteAsset(asset.id);
      refreshAssets();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete asset");
    }
  }

  const countryCount = assets ? new Set(assets.map((a) => a.country)).size : 0;
  const countryList = assets ? Array.from(new Set(assets.map((a) => a.country))).sort().join(", ") : "";
  const mappedPercent =
    assets && assets.length > 0
      ? Math.round((assets.filter((a) => a.valueChainStageId).length / assets.length) * 100)
      : 0;

  return (
    <div className="flex flex-col gap-6">
      <AssessmentTabs />

      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-heading font-bold leading-7">Input data — sites and assets</h1>
          <p className="max-w-2xl text-xs leading-4 text-muted-foreground">
            Add each asset separately when it sits in a different place or does a different job. The value
            chain stage is filled in from the asset type.
          </p>
        </div>
        {assets && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card px-3.5 py-2 text-xs shadow-sm">
            <span className="font-bold text-primary">
              {assets.length} Asset{assets.length === 1 ? "" : "s"} Registered
            </span>
            <span className="text-border">|</span>
            <span className="text-muted-foreground">
              {countryCount} {countryCount === 1 ? "Geography" : "Geographies"}
              {countryList && ` (${countryList})`}
            </span>
            <span className="text-border">|</span>
            <span className="font-bold text-primary">{mappedPercent}% Value Chain Mapped</span>
          </div>
        )}
      </div>

      <Card id="site-data" className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border bg-background/60 px-4 py-3">
          <CardTitle className="text-base">Site data</CardTitle>
          <div className="flex gap-2">
            <ExcelUploadDialog assetTypes={assetTypes} stages={stages} onImported={refreshAssets} />
            {editingAsset === undefined && (
              <Button variant="outline" onClick={() => setEditingAsset(null)}>
                + Add Asset
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-6 p-4">
          {error && <p className="text-sm font-medium text-destructive">{error}</p>}
          {!assets && !error && <p className="text-sm text-muted-foreground">Loading...</p>}
          {assets && (
            <AssetList
              assets={assets}
              assetTypes={assetTypes}
              stages={stages}
              onEdit={(asset) => setEditingAsset(asset)}
              onDelete={handleDelete}
            />
          )}

          {editingAsset !== undefined && (
            <AssetForm
              key={editingAsset === null ? "add" : editingAsset.id}
              asset={editingAsset ?? undefined}
              onSaved={handleFormSaved}
              onCancel={() => setEditingAsset(undefined)}
            />
          )}
        </CardContent>
      </Card>

      <div className="flex items-start gap-3 rounded-xl border border-primary/10 bg-primary/5 p-4">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
          <Info className="h-4 w-4" />
        </span>
        <p className="text-xs leading-5">
          <span className="font-bold">Importance:</span>{" "}
          <span className="text-muted-foreground">
            if this asset stops working, how much does the business suffer? One answer per asset, not one
            per hazard. It feeds into the Vulnerability component of the risk score, and orders the results
            and report takeaways.
          </span>
        </p>
      </div>

      <ValueChainImportanceForm />

      <div className="flex items-center justify-between border-t border-border pt-4">
        <Button variant="outline" asChild>
          <Link href="/dashboard">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Dashboard
          </Link>
        </Button>
        <Button asChild>
          <Link href="/assessment/results">
            Continue to Results Overview
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

export default Assessment;
